import { useMemo, useState } from "react";
import api, { extractErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { computeCustomForm, defaultFormValues } from "@/lib/customForm";
import { money } from "@/lib/money";

export default function CustomProductForm({ product, variant, slug, primary, onAdd, disabled, disabledLabel }) {
  const form = product.custom_form;
  const [values, setValues] = useState(() => defaultFormValues(form));
  const [uploading, setUploading] = useState(null);
  const base = Number(variant?.price || product.price || 0);
  const symbol = product.currency_symbol || "₹";

  const priced = useMemo(() => computeCustomForm(form, values, base), [form, values, base]);

  const setField = (id, v) => setValues((prev) => ({ ...prev, [id]: v }));

  const upload = async (fieldId, file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Image exceeds 2MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(fieldId);
    try {
      const { data } = await api.post(`/store/${slug}/custom-form/upload`, fd);
      setField(fieldId, data.url);
      toast.success("Image uploaded");
    } catch (e) { toast.error(extractErr(e)); }
    finally { setUploading(null); }
  };

  const submit = () => {
    if (disabled) { toast.error(disabledLabel || "Store is currently offline to accept any order"); return; }
    if (!priced.ok) { toast.error(priced.error); return; }
    onAdd({ values, ...priced });
  };

  return (
    <div className="mt-6 rounded-2xl border border-stone-200 bg-white p-5 space-y-4 shadow-sm" data-testid="custom-form-card">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-bold text-stone-900">Get Started</h2>
      </div>
      {(form.fields || []).map((field) => (
        <div key={field.id} className="space-y-1.5">
          <label className="text-sm font-medium text-stone-800">
            {field.label}
            {field.required ? <span className="text-red-500"> *</span> : null}
            {field.help ? <span className="ml-1 text-xs text-stone-400 font-normal">({field.help})</span> : null}
          </label>
          {field.type === "paid_name" && (
            <Input
              data-testid={`cf-field-${field.id}`}
              value={values[field.id] || ""}
              onChange={(e) => setField(field.id, e.target.value)}
              placeholder={field.price ? `+${symbol}${field.price}` : ""}
            />
          )}
          {(field.type === "paid_dropdown" || field.type === "quantity") && (
            <select
              className="w-full h-11 rounded-lg border border-stone-200 px-3 text-sm bg-white"
              data-testid={`cf-field-${field.id}`}
              value={values[field.id] || ""}
              onChange={(e) => setField(field.id, e.target.value)}
            >
              {(field.options || []).map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}{o.qty ? ` · ${o.qty} pcs` : ""} · {money(o.price, symbol)}
                </option>
              ))}
            </select>
          )}
          {field.type === "paid_image" && (
            <div className="flex items-center gap-2">
              <label className="text-sm text-emerald-800 underline cursor-pointer">
                {uploading === field.id ? "Uploading…" : (values[field.id] ? "Replace image" : "Upload image")}
                <input type="file" hidden accept="image/*" onChange={(e) => upload(field.id, e.target.files?.[0])} />
              </label>
              {field.price ? <span className="text-xs text-stone-500">+{money(field.price, symbol)}</span> : null}
            </div>
          )}
        </div>
      ))}
      <div className="pt-2 border-t border-stone-100">
        <div className="flex justify-between items-baseline">
          <span className="text-sm text-stone-600">Total (GST Incl.):</span>
          <span className="font-mono-nums text-2xl font-bold" data-testid="custom-form-total">{money(priced.line_total, symbol)}</span>
        </div>
        <div className="text-xs text-stone-500 mt-0.5" data-testid="custom-form-unit">Unit Price: {money(priced.unit_price, symbol)}</div>
      </div>
      <Button type="button" onClick={submit} disabled={disabled} className="w-full h-12 rounded-full text-white font-semibold" style={{ background: primary }} data-testid="custom-form-cta">
        {disabled ? (disabledLabel || "Store is currently offline to accept any order") : (form.cta || "Get Started")}
      </Button>
    </div>
  );
}
